import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { TextField, Label } from "react-aria-components";
import { Loader2 } from "lucide-react";
import type { FacilityNode } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Dialog, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useRenameFacility } from "./useFacilityMutations.js";
import { useShowError } from "../../error/ErrorProvider.js";

export interface RenameFacilityDialogProps {
  /** The facility to rename. Mount-per-open (node != null ⇒ open) keeps the draft fresh. */
  node: FacilityNode;
  session: AuthSession | null;
  onClose: () => void;
  /** Announce the successful rename to the caller's aria-live region. */
  onRenamed?: (name: string) => void;
}

/**
 * Rename a facility via a small modal (ADR-006 §1). The drill-down counterpart to the classic tree's INLINE rename —
 * the rail cards have no edit-in-place field, so a focused dialog is the parity path. Mirrors the classic save logic
 * exactly: trims, no-ops on an unchanged/empty name, and a **synchronous ref-guard** rejects a same-tick double-submit
 * before `isPending` can re-render (the additive/PATCH boundary — panel). Errors route to the shared error dialog; the
 * tree query refetches on success (new name + counts).
 */
export function RenameFacilityDialog({ node, session, onClose, onRenamed }: RenameFacilityDialogProps) {
  const { t } = useTranslation();
  const rename = useRenameFacility(session);
  const showError = useShowError();
  const [name, setName] = useState(node.name);
  const submitting = useRef(false);

  const submit = () => {
    if (submitting.current) return;
    const next = name.trim();
    if (next === "" || next === node.name) {
      onClose();
      return;
    }
    submitting.current = true;
    rename.mutate(
      { id: node.id, name: next },
      {
        onSuccess: () => {
          onRenamed?.(next);
          onClose();
        },
        onError: (err) => {
          // Keep the dialog OPEN (draft intact) so a recoverable error (409 name conflict / 400) can be retried without
          // retyping — mirrors classic's inline rename (onError: showError, no teardown). onSettled resets the guard.
          showError(err);
        },
        onSettled: () => {
          submitting.current = false;
        },
      },
    );
  };

  return (
    <Dialog isOpen isDismissable={!rename.isPending} onOpenChange={(open) => !open && !rename.isPending && onClose()}>
      <DialogTitle>{t("facilityManagement.rename.title")}</DialogTitle>
      <TextField
        aria-label={t("facilityManagement.rename.nameLabel")}
        value={name}
        onChange={setName}
        maxLength={255}
        autoFocus
        className="flex flex-col gap-1.5"
      >
        <Label className="text-sm font-medium">{t("facilityManagement.rename.nameField")}</Label>
        <Input
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
        />
      </TextField>
      <DialogFooter>
        <Button variant="ghost" onPress={onClose} isDisabled={rename.isPending}>
          {t("other.cancelButton")}
        </Button>
        <Button onPress={submit} isDisabled={rename.isPending || name.trim() === ""}>
          {rename.isPending ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
          {t("other.saveButtonLabel")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
